/**
 * What the tour reports (trial telemetry, docs/architecture/telemetry-catalog.md): where it was started, its reading
 * window, each step reached and how, where it was left, its end, and the offer of it on their own first job. Names
 * and attributes are code words and numbers only: the tour's step ids, where it was started, counts and times. The
 * job's id goes to main, which sends only the job's kind (tour, or own for a tour on their own job).
 *
 * The rules live here, apart from the screens, so they can be tested alone (tests/telemetry-tests/tour-hints.cjs).
 * Every write of the tour's place comes through `written` (tour.ts's onTourWrite), which compares it with the one
 * before: a step reached, the tour left, or its last step reached.
 */
import { reportEvent } from './telemetry';
import {
  onTourWrite,
  readTour,
  tourPosition,
  type TourState,
  type TourStepId,
  type TourVia,
} from './tour';
import type { ReaderProblem } from './not-read';
import type { TelemetryCatalogAttrs, TelemetryEventName } from '../shared/telemetry-catalog';
import type { SampleReaderDownloadProgress } from '../shared/desktop-api';

/** Where the tour was started: the welcome's link, New job's footer, Settings › Appearance, a capturepoint://tour link, or their own first job's offer. */
export type TourStartFrom =
  'welcome' | 'new-job' | 'settings' | 'link' | 'own-offer' | 'hint' | 'other';

export type TelemetryDeps = {
  reportEvent: (
    name: TelemetryEventName,
    options?: { attrs?: TelemetryCatalogAttrs; jobId?: string; durationMs?: number },
  ) => void;
  now: () => number;
};

/** The catalog's name for a step: Needs review's two wordings are one step, step 7. */
export const tourStepName = (step: TourStepId): Exclude<TourStepId, 'review-wait'> =>
  step === 'review-wait' ? 'review' : step;

/** The tour has begun: its first card is up (the sample tour once its five are read; a tour on their own job at once). */
const begun = (state: TourState | null): state is TourState => Boolean(state?.ready);
const sameTour = (a: TourState | null, b: TourState | null): boolean =>
  Boolean(a && b && a.jobId === b.jobId && a.startedAt === b.startedAt);
const sinceStart = (state: TourState, now: number): number | undefined => {
  const at = Date.parse(state.startedAt);
  return Number.isFinite(at) && now >= at ? now - at : undefined;
};

// standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
export function createTourTelemetry(deps: TelemetryDeps, initial: TourState | null = null) {
  /** The tour's place as last written, and when the step now shown began in this window (unknown after a restart). */
  let last: TourState | null = initial;
  let stepAt: number | undefined;
  const finished = new Set<string>();
  const ready = new Set<string>();
  const problems = new Set<string>();
  let offerShown = false;
  /** The latest tour's sample reader download, in ms (0: none was needed); undefined while not known. */
  let readerDownloadMs: number | undefined;

  const reached = (state: TourState, now: number, via?: TourVia, msPrevious?: number) => {
    const step = tourStepName(state.step);
    deps.reportEvent(`tour.step:${step}`, {
      jobId: state.jobId,
      attrs: {
        n: tourPosition(state.step).index + 1,
        own: Boolean(state.own),
        ...(via ? { via } : {}),
        ...(msPrevious !== undefined ? { ms_prev_step: msPrevious } : {}),
      },
    });
    stepAt = now;
    // Its last step reached is the tour finished: the product counts it as taken and watched there (TourCard).
    const key = `${state.jobId}|${state.startedAt}`;
    if (state.step === 'turn' && !finished.has(key)) {
      finished.add(key);
      const ms = sinceStart(state, now);
      deps.reportEvent('tour.finished', {
        jobId: state.jobId,
        attrs: { own: Boolean(state.own), ...(ms !== undefined ? { ms } : {}) },
      });
    }
  };

  /** The tour ended before its last step: left while its five were read, or at the step its card showed. */
  const left = (state: TourState, now: number) => {
    if (begun(state) && state.step === 'turn') return;
    const where = begun(state) ? tourStepName(state.step) : 'reading';
    const ms = sinceStart(state, now);
    deps.reportEvent(`tour.left:${where}`, {
      jobId: state.jobId,
      attrs: {
        own: Boolean(state.own),
        at: begun(state) ? 'card' : 'reading',
        ...(ms !== undefined ? { ms_in_tour: ms } : {}),
      },
    });
  };

  return {
    /** Every write of the tour's place (writeTour), with how a step change came about. */
    written(next: TourState | null, via?: TourVia): void {
      const before = last;
      last = next;
      const now = deps.now();
      if (before && !sameTour(before, next)) {
        left(before, now);
        stepAt = undefined;
      }
      if (!begun(next)) return;
      if (!begun(before) || !sameTour(before, next)) {
        reached(next, now);
        return;
      }
      if (before.step === next.step) return;
      reached(next, now, via, stepAt !== undefined ? now - stepAt : undefined);
    },

    /** A tour started, once its job exists (main then reaches funnel tour_started). `again`: they had watched one before. */
    started(from: TourStartFrom, { jobId, again }: { jobId: string; again: boolean }): void {
      deps.reportEvent(`tour.start:${from}`, { jobId, attrs: { again } });
    },

    /** "Your tour is ready": how long since the click, how many were read again, and the reader's troubles on the way. */
    ready({
      jobId,
      durationMs,
      readAgain,
      readerProblem,
    }: {
      jobId: string;
      durationMs?: number;
      readAgain: number;
      readerProblem: ReaderProblem | null;
    }): void {
      if (ready.has(jobId)) return;
      ready.add(jobId);
      deps.reportEvent('tour.ready', {
        jobId,
        ...(durationMs !== undefined && durationMs >= 0 ? { durationMs } : {}),
        attrs: {
          read_again: readAgain,
          reader_problem: readerProblem ?? 'none',
          ...(readerDownloadMs !== undefined ? { reader_download_ms: readerDownloadMs } : {}),
        },
      });
    },

    /** The reading window says the field reader can't read (not-read.ts): once per tour job and cause. */
    readerProblem(cause: ReaderProblem, jobId: string): void {
      if (problems.has(`${jobId}|${cause}`)) return;
      problems.add(`${jobId}|${cause}`);
      deps.reportEvent(`reader.problem:${cause}`, { jobId });
    },

    /** Their first own job offers the tour (shown once per session), and their answer. */
    offer(answer: 'shown' | 'yes' | 'no', jobId: string): void {
      if (answer === 'shown') {
        if (offerShown) return;
        offerShown = true;
      }
      deps.reportEvent(`tour.offer:${answer}`, { jobId });
    },

    /**
     * Times the sample reader download inside the tour job's import, from the progress main sends: from the first
     * "downloading" to "done" (which main sends once the import is over, downloaded or not); 0 when none was needed.
     */
    watchReaderDownload(
      corpus: string,
      subscribe:
        ((callback: (progress: SampleReaderDownloadProgress) => void) => () => void) | undefined,
    ): () => void {
      readerDownloadMs = undefined;
      if (!subscribe) return () => undefined;
      let began: number | undefined;
      try {
        return subscribe((progress) => {
          if (progress.corpus !== corpus) return;
          if (progress.state === 'downloading') began ??= deps.now();
          else if (progress.state === 'done')
            readerDownloadMs = began === undefined ? 0 : deps.now() - began;
        });
      } catch {
        return () => undefined;
      }
    },
  };
}

export type TourTelemetry = ReturnType<typeof createTourTelemetry>;

/** This window's tour telemetry: it hears every write of the tour's place from here on. */
export const tourTelemetry: TourTelemetry = createTourTelemetry(
  { reportEvent, now: () => Date.now() },
  readTour(),
);
onTourWrite((state, via) => tourTelemetry.written(state, via));
